import React, { useState, useEffect } from 'react';
import { Grid2, TextField, Box, Button, Container, Typography, CircularProgress, InputLabel, Select, MenuItem, FormControl } from '@mui/material';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css'; // Import default PhoneInput styles
import axios from 'axios';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

// List of common currencies (you can expand this as needed)
const currencyOptions = [
  { value: 'USD', label: 'US Dollar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'GBP', label: 'British Pound (GBP)' },
  { value: 'JPY', label: 'Japanese Yen (JPY)' },
  { value: 'CAD', label: 'Canadian Dollar (CAD)' },
  { value: 'AUD', label: 'Australian Dollar (AUD)' },
  { value: 'INR', label: 'Indian Rupee (INR)' },
  { value: 'PLN', label: 'Polish Zloty (PLN)' },
];

// Styled PhoneInput to match MUI TextField
const StyledPhoneInput = styled(PhoneInput)(({ theme, error }) => ({
  '& .PhoneInputInput': {
    padding: '16.5px 14px', // Match MUI TextField padding
    fontSize: '1rem',
    borderRadius: '4px',
    border: error ? `1px solid ${theme.palette.error.main}` : '1px solid rgba(0, 0, 0, 0.23)',
    width: '100%',
    outline: 'none',
    '&:hover': {
      borderColor: error ? theme.palette.error.main : 'rgba(0, 0, 0, 0.87)',
    },
    '&:focus': {
      borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
      borderWidth: 2,
    },
  },
  '& .PhoneInputCountry': {
    marginRight: theme.spacing(1),
  },
}));

const RentOfferForm = ({ apartmentId, token }) => {
  const { t } = useTranslation();
  const [rentOffer, setRentOffer] = useState({
    currency: 'USD', // Default value
    one_year_rent: 0,
    two_years_rent: 0,
    three_years_rent: 0,
    residential_fee: 0,
    bills_amount: 0,
    available_from: '', // Default empty string for date
    contact_phone: '',
    contact_email: '',
  });
  const [isNewEntry, setIsNewEntry] = useState(true); // Assume new entry by default
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    const fetchRentOffer = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/rentoffer/${apartmentId}/`);
        setRentOffer(response.data);
        setIsNewEntry(false);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          // Not found means it's a new entry
          setIsNewEntry(true);
        } else {
          setError('Error fetching rent offer details.');
          console.error('Error fetching rent offer data:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    if (apartmentId) {
      fetchRentOffer();
    }
  }, [apartmentId, token]);

  const handleChange = (field, value) => {
    setRentOffer((prevOffer) => ({
      ...prevOffer,
      [field]: field === 'available_from' ? value : String(value || ''),
    }));

    // Validate phone number when it changes
    if (field === 'contact_phone') {
      if (value && !/^\+?[1-9]\d{1,14}$/.test(value)) {
        setPhoneError('Please enter a valid phone number (e.g., +1234567890)');
      } else {
        setPhoneError('');
      }
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/api/rentoffer/${apartmentId}/`;
      const method = isNewEntry ? 'post' : 'put';
      const formData = new FormData();

      for (const key in rentOffer) {
        formData.append(key, rentOffer[key] || ''); // Ensure empty strings for null values
      }

      console.log('formData: ', formData);
      await axios[method](url, formData, {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('Rent offer saved successfully!');
    } catch (error) {
      console.error('Error saving rent offer data:', error);
      alert('Failed to save rent offer data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container>
      <Grid2 container spacing={2}>
        {/* Form Fields Section */}
        <Grid2 container item xs={12} spacing={2}>
          <Grid2 item xs={12} sm={6} md={4}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="currency-label">{t('rentOffer.currency')}</InputLabel>
              <Select
                labelId="currency-label"
                name="currency"
                value={rentOffer.currency || 'USD'} // Default to 'USD' if null
                onChange={(e) => handleChange('currency', e.target.value)}
                label={t('rentOffer.currency')}
              >
                {currencyOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
          <Grid2 item xs={12} sm={6} md={4}>
            <TextField
              label={t('rentOffer.oneYearRent')}
              name="one_year_rent"
              type="number"
              value={rentOffer.one_year_rent || 0} // Default to 0
              onChange={(e) => handleChange('one_year_rent', e.target.value)}
              fullWidth
              margin="normal"
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid2>
          <Grid2 item xs={12} sm={6} md={4}>
            <TextField
              label={t('rentOffer.twoYearsRent')}
              name="two_years_rent"
              type="number"
              value={rentOffer.two_years_rent || 0} // Default to 0
              onChange={(e) => handleChange('two_years_rent', e.target.value)}
              fullWidth
              margin="normal"
            />
          </Grid2>
          <Grid2 item xs={12} sm={6} md={4}>
            <TextField
              label={t('rentOffer.threeYearsRent')}
              name="three_years_rent"
              type="number"
              value={rentOffer.three_years_rent || 0} // Default to 0
              onChange={(e) => handleChange('three_years_rent', e.target.value)}
              fullWidth
              margin="normal"
            />
          </Grid2>
          <Grid2 item xs={12} sm={6} md={4}>
            <TextField
              label={t('rentOffer.residentialFee')}
              name="residential_fee"
              type="number"
              value={rentOffer.residential_fee || 0} // Default to 0
              onChange={(e) => handleChange('residential_fee', e.target.value)}
              fullWidth
              margin="normal"
            />
          </Grid2>
          <Grid2 item xs={12} sm={6} md={4}>
            <TextField
              label={t('rentOffer.bills')}
              name="bills_amount"
              type="number"
              value={rentOffer.bills_amount || 0} // Default to 0
              onChange={(e) => handleChange('bills_amount', e.target.value)}
              fullWidth
              margin="normal"
            />
          </Grid2>
          <Grid2 item xs={12} sm={6} md={4}>
            <TextField
              label={t('rentOffer.availableFrom')}
              name="available_from"
              type="date"
              value={rentOffer.available_from || ''} // Ensure empty string if value is null
              onChange={(e) => handleChange('available_from', e.target.value)}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </Grid2>
          <Grid2 item xs={12} sm={6} md={4}>
            <TextField
              label={t('rentOffer.contactEmail')}
              name="contact_email"
              value={rentOffer.contact_email || ''} // Ensure empty string if value is null
              onChange={(e) => handleChange('contact_email', e.target.value)}
              fullWidth
              margin="normal"
            />
          </Grid2>
          <Grid2 item xs={12} sm={6} md={4}>
            <Box sx={{ mt: 2 }}>
              <InputLabel shrink htmlFor="contact_phone">{t('rentOffer.contactNumber')}</InputLabel>
              <StyledPhoneInput
                id="contact_phone"
                name="contact_phone"
                value={rentOffer.contact_phone || ''}
                onChange={(value) => handleChange('contact_phone', value)}
                defaultCountry="US"
                international
                countryCallingCodeEditable={false}
                placeholder={t('rentOffer.phonePlaceholder')}
                error={!!phoneError}
              />
              {phoneError && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {phoneError}
                </Typography>
              )}
            </Box>
          </Grid2>
        </Grid2>

        {/* Error Message Section */}
        {error && (
          <Grid2 item xs={12}>
            <div style={{ color: 'red' }}>{error}</div>
          </Grid2>
        )}

        {/* Save Button Section */}
        <Grid2 item xs={12}>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            {t('rentOffer.save')}
          </Button>
        </Grid2>
      </Grid2>
    </Container>
  );
};

export default RentOfferForm;